import { cacheResponse } from '~/composables/useHlsPlayer/videoSegmentCache';

/**
 * Returns the video URL with a cache-buster (`_cb=1`) to avoid stale CORS preflight cache.
 * Ensures URL is absolute — critical for iOS Safari HLS manifest resolution.
 */
export function getProxiedVideoUrl(
	videoUrl: string | null | undefined
): string | null {
	if (videoUrl == null || videoUrl === '') return null;

	// Convert relative URL to absolute (critical for SSR on iOS)
	const absoluteUrl = (() => {
		if (typeof window === 'undefined' || videoUrl.startsWith('http')) {
			return videoUrl;
		}
		try {
			return new URL(videoUrl, window.location.href).href;
		} catch {
			// If URL parsing fails, use as-is
			return videoUrl;
		}
	})();

	const separator = absoluteUrl.includes('?') ? '&' : '?';
	return `${absoluteUrl}${separator}_cb=1`;
}

export interface ThumbnailOptions {
	width?: number;
	height?: number;
	quality?: number;
	format?: string;
}

/**
 * Composable that returns a function to generate optimized thumbnail URLs
 * via NuxtImage's configured provider (e.g. Netlify Image CDN in production).
 * Must be called inside `<script setup>` or another composable.
 */
export function useOptimizedThumbnail() {
	const img = useImage();

	return (
		thumbnailUrl: string | null,
		options: ThumbnailOptions = {}
	): string | null => {
		if (!thumbnailUrl) return null;

		const { width, height, quality = 80, format = 'jpg' } = options;

		return img(thumbnailUrl, {
			format,
			quality,
			...(width && { width }),
			...(height && { height }),
			...((width || height) && { fit: 'cover' })
		});
	};
}

export type VideoOrientation = 'portrait' | 'landscape' | 'square';
export const COMPACT_HERO_VIDEO_MAX_WIDTH = 700;

/**
 * Determines video orientation based on width and height.
 * @param width - Video width in pixels
 * @param height - Video height in pixels
 * @returns 'portrait' if height > width, 'landscape' if width > height, 'square' if equal
 */
export function getVideoOrientation(
	width: number | undefined,
	height: number | undefined
): VideoOrientation {
	if (!width || !height || width === 0 || height === 0) {
		// Default to portrait for mobile-first video reels
		return 'portrait';
	}

	if (height > width) return 'portrait';
	if (width > height) return 'landscape';
	return 'square';
}

/**
 * Checks if the video is in portrait orientation (height > width).
 * @param width - Video width in pixels
 * @param height - Video height in pixels
 * @returns true if video is portrait, false otherwise
 */
export function isPortraitVideo(
	width: number | undefined,
	height: number | undefined
): boolean {
	return getVideoOrientation(width, height) === 'portrait';
}

/**
 * Checks if the video is in landscape orientation (width > height).
 * @param width - Video width in pixels
 * @param height - Video height in pixels
 * @returns true if video is landscape, false otherwise
 */
export function isLandscapeVideo(
	width: number | undefined,
	height: number | undefined
): boolean {
	return getVideoOrientation(width, height) === 'landscape';
}

/**
 * Hero videos with smaller widths should use the compact first-slot layout,
 * even if the encoded orientation metadata is not strictly portrait.
 */
export function shouldUseCompactHeroVideoLayout(
	width: number | undefined,
	height: number | undefined
): boolean {
	if (width && width > 0 && width <= COMPACT_HERO_VIDEO_MAX_WIDTH) {
		return true;
	}

	return isPortraitVideo(width, height);
}

// ---------------------------------------------------------------------------
// HLS manifest parsing & segment prefetch helpers
// ---------------------------------------------------------------------------

interface HlsVariant {
	bandwidth: number;
	url: string;
}

/**
 * Resolves a potentially relative URL against a base URL.
 * Follows HLS spec: relative paths inherit the base origin + directory,
 * but NOT query strings or fragments from the parent.
 */
function resolveUrl(base: string, relative: string): string {
	if (relative.startsWith('http://') || relative.startsWith('https://')) {
		return relative;
	}
	const baseUrl = new URL(base);
	if (relative.startsWith('/')) {
		return `${baseUrl.origin}${relative}`;
	}
	const basePath = baseUrl.pathname.substring(
		0,
		baseUrl.pathname.lastIndexOf('/') + 1
	);
	return `${baseUrl.origin}${basePath}${relative}`;
}

/**
 * Parses an HLS master playlist and returns variant streams sorted by bandwidth (ascending).
 * Returns empty array if the playlist is a media playlist (no variants).
 */
function parseMasterPlaylist(text: string): HlsVariant[] {
	const lines = text.split('\n').map(l => l.trim());
	const variants: HlsVariant[] = [];

	for (let i = 0; i < lines.length - 1; i++) {
		const line = lines[i] ?? '';
		if (!line.startsWith('#EXT-X-STREAM-INF:')) continue;

		const bwMatch = line.match(/BANDWIDTH=(\d+)/);
		if (!bwMatch) continue;

		// The URL is the next non-empty, non-comment line
		const url = lines[i + 1] ?? '';
		if (!url || url.startsWith('#')) continue;

		variants.push({ bandwidth: Number(bwMatch[1]), url });
	}

	return variants.sort((a, b) => a.bandwidth - b.bandwidth);
}

/**
 * Parses an HLS media playlist and returns the first N segment URLs.
 */
function parseMediaPlaylist(text: string, maxSegments: number = 1): string[] {
	const lines = text.split('\n').map(l => l.trim());
	const segments: string[] = [];

	for (const line of lines) {
		if (line.startsWith('#') || line === '') continue;
		segments.push(line);
		if (segments.length >= maxSegments) break;
	}

	return segments;
}

/**
 * Extracts the `v=HASH` version parameter from a URL.
 * Shaka's custom scheme handler (`getVersionedUrl`) appends this to all
 * `.m3u8` and `.ts` requests, so our prefetched URLs must include it too
 * for the browser/CDN cache to hit.
 */
function extractVersionParam(url: string): string {
	const match = url.match(/[?&]v=([^&]+)/);
	return match?.[1] ?? '';
}

/** Appends `?v=HASH` to a URL if a version hash is available. */
function appendVersion(url: string, versionHash: string): string {
	if (!versionHash) return url;
	const separator = url.includes('?') ? '&' : '?';
	return `${url}${separator}v=${versionHash}`;
}

/**
 * Fetches a URL and stores the response in the shared CacheStorage so that
 * Shaka Player's scheme handler finds it and skips the network request.
 * Clones the response upfront so the caller can safely consume the original.
 */
async function fetchAndCache(
	url: string,
	fetchOpts: RequestInit
): Promise<Response> {
	const response = await fetch(url, fetchOpts);
	// Clone immediately — cacheResponse is async and may run after the caller
	// has already consumed the original body.
	cacheResponse(url, response.clone()).catch(() => {});
	return response;
}

/**
 * Prefetches the first segment of an HLS video variant.
 * Responses are stored in CacheStorage so Shaka reuses them instead of
 * re-downloading. Returns the resolved variant playlist URL (used by iOS
 * quality cache to bypass AVPlayer's conservative quality selection).
 *
 * @param quality - 'lowest' for safe preload on unknown networks (default);
 *                  'highest' for 1080p preload when network is proven good.
 * @param maxSegmentBps - when set, the segment download is skipped if the
 *                  chosen variant declares a higher BANDWIDTH. Guards slow
 *                  networks against old single-variant videos whose "lowest"
 *                  rung is a 4-9 Mbps original (a 4+ MB first segment that
 *                  would hog the pipe for 30s+ on 3G). Manifests still warm.
 */
export async function prefetchFirstSegment(
	masterUrl: string,
	signal: AbortSignal,
	quality: 'lowest' | 'highest' = 'lowest',
	maxSegmentBps?: number
): Promise<string | null> {
	const fetchOpts = { priority: 'low', signal } as RequestInit;
	const versionHash = extractVersionParam(masterUrl);

	// 1. Fetch & parse master playlist — also cache for Shaka reuse
	const masterResponse = await fetchAndCache(masterUrl, fetchOpts);
	const masterText = await masterResponse.text();
	const variants = parseMasterPlaylist(masterText);

	if (variants.length === 0) {
		// Single-variant stream — master IS the media playlist. Bandwidth is
		// unknown here, so a maxSegmentBps caller opts out of the segment.
		if (maxSegmentBps !== undefined) return null;
		const segments = parseMediaPlaylist(masterText, 1);
		const firstSegment = segments[0];
		if (firstSegment) {
			await fetchAndCache(
				appendVersion(resolveUrl(masterUrl, firstSegment), versionHash),
				fetchOpts
			);
		}
		return null;
	}

	// 2. Pick variant by quality (sorted ascending by bandwidth)
	const chosen =
		quality === 'highest' ? variants[variants.length - 1] : variants[0];
	if (!chosen) return null;

	const variantUrl = appendVersion(
		resolveUrl(masterUrl, chosen.url),
		versionHash
	);

	// 3. Fetch & parse variant playlist — also cache for Shaka reuse
	const variantResponse = await fetchAndCache(variantUrl, fetchOpts);
	const variantText = await variantResponse.text();
	const segments = parseMediaPlaylist(variantText, 1);

	const firstSegment = segments[0];
	if (!firstSegment) return variantUrl;

	// 4. Prefetch the first segment (unless the variant is too heavy for the
	// current network — manifests are warmed above either way)
	if (maxSegmentBps !== undefined && chosen.bandwidth > maxSegmentBps) {
		return getProxiedVideoUrl(variantUrl) ?? variantUrl;
	}
	await fetchAndCache(
		appendVersion(resolveUrl(variantUrl, firstSegment), versionHash),
		fetchOpts
	);

	// Return the proxied variant URL for iOS quality cache
	return getProxiedVideoUrl(variantUrl) ?? variantUrl;
}

/**
 * Resolves the URL of the first segment of the lowest-quality variant.
 * Does NOT download the segment — used as a probe target for bandwidth measurement.
 */
export async function resolveFirstSegmentUrl(
	masterUrl: string,
	signal: AbortSignal
): Promise<string | null> {
	try {
		const fetchOpts = { priority: 'high', signal } as RequestInit;
		const versionHash = extractVersionParam(masterUrl);

		const masterResponse = await fetchAndCache(masterUrl, fetchOpts);
		const masterText = await masterResponse.text();
		const variants = parseMasterPlaylist(masterText);

		let firstSegmentRaw: string | undefined;
		let baseUrl: string;

		if (variants.length === 0) {
			firstSegmentRaw = parseMediaPlaylist(masterText, 1)[0];
			baseUrl = masterUrl;
		} else {
			const lowest = variants[0];
			if (!lowest) return null;
			const variantUrl = appendVersion(
				resolveUrl(masterUrl, lowest.url),
				versionHash
			);
			const variantResponse = await fetchAndCache(variantUrl, fetchOpts);
			const variantText = await variantResponse.text();
			firstSegmentRaw = parseMediaPlaylist(variantText, 1)[0];
			baseUrl = variantUrl;
		}

		if (!firstSegmentRaw) return null;
		return getProxiedVideoUrl(
			appendVersion(resolveUrl(baseUrl, firstSegmentRaw), versionHash)
		);
	} catch {
		return null;
	}
}

export interface HlsWarmupUrls {
	// Null when the stored video URL already IS the media playlist (old
	// single-variant videos) — there is no separate variant playlist to preload.
	variantPlaylistUrl: string | null;
	firstSegmentUrl: string | null;
}

// Manifests are immutable (URL carries the assembly id + version hash), so a
// resolved chain never changes. Two-tier cache: per-process memory (L1, 0ms)
// backed by the shared Redis (L2) so fresh instances start warm across the
// fleet. Failures are NOT cached: a transient CDN error must not pin a video
// to "no hints".
const warmupCache = new Map<string, HlsWarmupUrls>();
const WARMUP_CACHE_MAX_ENTRIES = 500;
const WARMUP_REDIS_PREFIX = 'hls:warmup:';
const WARMUP_REDIS_TTL_SECONDS = 7 * 24 * 60 * 60; // 7 days
// A warmup cache read is an optimization — never worth more TTFB than this.
const WARMUP_REDIS_READ_TIMEOUT_MS = 150;

interface RedisLike {
	status: string;
	get(key: string): Promise<string | null>;
	set(key: string, value: string, mode: 'EX', ttl: number): Promise<unknown>;
}

// Same global-client pattern as server/runtime/feature-flags.ts — the shared
// ioredis instance is exposed on globalThis by the server plugin. Null on the
// client and whenever Redis is unavailable (never block SSR on Redis).
function getServerRedis(): RedisLike | null {
	if (!import.meta.server) return null;
	const redis = (globalThis as { __redis?: RedisLike }).__redis;
	return redis && redis.status === 'ready' ? redis : null;
}

function storeWarmupResult(masterUrl: string, resolved: HlsWarmupUrls): void {
	if (warmupCache.size >= WARMUP_CACHE_MAX_ENTRIES) {
		const oldest = warmupCache.keys().next().value;
		if (oldest !== undefined) warmupCache.delete(oldest);
	}
	warmupCache.set(masterUrl, resolved);

	getServerRedis()
		?.set(
			`${WARMUP_REDIS_PREFIX}${masterUrl}`,
			JSON.stringify(resolved),
			'EX',
			WARMUP_REDIS_TTL_SECONDS
		)
		.catch(() => {});
}

async function readWarmupCache(
	masterUrl: string
): Promise<HlsWarmupUrls | null> {
	const memory = warmupCache.get(masterUrl);
	if (memory) return memory;

	const redis = getServerRedis();
	if (!redis) return null;
	try {
		// This read is awaited during SSR, and the caller's 800ms warmup signal
		// does not cover it — a "ready" Redis with high latency must degrade to
		// a cache miss instead of holding the page's TTFB.
		const raw = await Promise.race([
			redis.get(`${WARMUP_REDIS_PREFIX}${masterUrl}`),
			new Promise<null>(resolve =>
				setTimeout(resolve, WARMUP_REDIS_READ_TIMEOUT_MS, null)
			)
		]);
		if (!raw) return null;
		const parsed = JSON.parse(raw) as HlsWarmupUrls;
		// variantPlaylistUrl is null by design for single-variant videos, so
		// validate the object shape instead of that nullable field.
		if (!parsed || typeof parsed !== 'object' || !('firstSegmentUrl' in parsed))
			return null;
		warmupCache.set(masterUrl, parsed);
		return parsed;
	} catch {
		return null;
	}
}

/**
 * Server-side resolver for SSR head preloads: fetches the master manifest and
 * the lowest-bandwidth variant playlist, returning their URLs so the page can
 * emit <link rel="preload"> hints. URLs carry the v=HASH version (matching
 * Shaka's request URLs exactly) but NOT _cb — the preload cache matches by
 * exact URL and Shaka's scheme handler does not append _cb.
 *
 * Never throws — returns null on any failure so SSR renders without hints.
 */
export async function resolveHlsWarmupUrls(
	masterUrl: string,
	signal: AbortSignal
): Promise<HlsWarmupUrls | null> {
	const cached = await readWarmupCache(masterUrl);
	if (cached) return cached;

	try {
		const versionHash = extractVersionParam(masterUrl);

		const masterResponse = await fetch(masterUrl, { signal });
		if (!masterResponse.ok) return null;
		const masterText = await masterResponse.text();
		const variants = parseMasterPlaylist(masterText);
		const lowest = variants[0];

		// Old single-variant videos store the media playlist URL directly — no
		// variant chain to walk, but the first segment is right there.
		if (!lowest) {
			const directSegment = parseMediaPlaylist(masterText, 1)[0];
			if (!directSegment) return null;
			const resolved: HlsWarmupUrls = {
				variantPlaylistUrl: null,
				firstSegmentUrl: appendVersion(
					resolveUrl(masterUrl, directSegment),
					versionHash
				)
			};
			storeWarmupResult(masterUrl, resolved);
			return resolved;
		}

		const variantPlaylistUrl = appendVersion(
			resolveUrl(masterUrl, lowest.url),
			versionHash
		);

		const variantResponse = await fetch(variantPlaylistUrl, { signal });
		if (!variantResponse.ok)
			return { variantPlaylistUrl, firstSegmentUrl: null };
		const firstSegment = parseMediaPlaylist(await variantResponse.text(), 1)[0];

		const resolved: HlsWarmupUrls = {
			variantPlaylistUrl,
			firstSegmentUrl: firstSegment
				? appendVersion(
						resolveUrl(variantPlaylistUrl, firstSegment),
						versionHash
					)
				: null
		};

		storeWarmupResult(masterUrl, resolved);

		return resolved;
	} catch {
		return null;
	}
}

/**
 * Fetches the master manifest and returns variant playlist URLs sorted by
 * bandwidth ascending (lowest quality first). Each URL has _cb=1 appended so
 * CORS preflight caching is bypassed consistently with the rest of the app.
 *
 * Used by the iOS quality-upgrade logic in HlsVideoPlayer: the caller steps
 * through the array one entry at a time as the network proves itself capable.
 * Returns an empty array when the URL is already a single-variant (media)
 * playlist or when the fetch fails.
 */
export async function resolveVariantUrls(
	masterUrl: string,
	signal: AbortSignal
): Promise<string[]> {
	try {
		const response = await fetch(masterUrl, {
			signal,
			priority: 'low'
		} as RequestInit);
		if (!response.ok) return [];

		const text = await response.text();
		const variants = parseMasterPlaylist(text);
		if (variants.length === 0) return [];

		const versionHash = extractVersionParam(masterUrl);
		return variants.map(v => {
			const resolved = appendVersion(resolveUrl(masterUrl, v.url), versionHash);
			return getProxiedVideoUrl(resolved)!;
		});
	} catch {
		return [];
	}
}

/**
 * Composable for video URL management.
 * Provides reactive utilities for video URLs.
 */
export function useVideoUrls() {
	return {
		getProxiedVideoUrl,
		getOptimizedThumbnailUrl: useOptimizedThumbnail(),
		getVideoOrientation,
		isPortraitVideo,
		isLandscapeVideo,
		prefetchFirstSegment
	};
}
